import { useState } from 'react';
import { useNavigate } from 'react-router';

import { FooterNavigator } from '../Footer/FooterNavigator';
import { Icon, Button } from '../UI';
import { Modal } from '../Modal';

import { Nav } from './NewStrategyNav';
import { Skip } from './Skip';

import { useNewStrategyContext } from './NewStrategyContext';
import { useUserStrategies } from '../../context/UserStrategies';
import { useAuth } from '../../context/Auth';

import { formatAWSDate } from '../../helpers/functions';

import { FooterNavigatorProps } from '../Footer/types';
import { Customize } from './Steps/2/Customize';
import { useTranslation } from 'react-i18next';
import { Start } from './Steps/1/Start';
import { ChooseImportMode } from './Steps/3/ChooseImportMode';
import { Summary } from './Steps/5/Summary';
import { DuplicateOrImport } from './Steps/4/DuplicateOrImport';
import { logger } from '../../helpers/logger';

export const NewStrategy = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    closeConfirmModal,
    closeModal,
    confirmElement,
    createStrategyAssets,
    createStrategy,
    deleteStrategyAssets,
    currentStep,
    dispatch,
    flow,
    flowSteps,
    isDisabledNext,
    mode,
    newStrategy,
    setStrategyStatus,
    updateStrategy,
    updateWorkspace,
    workspacesList,
  } = useNewStrategyContext();

  const { user } = useAuth();
  const { draftCloneStrategy, refetch: refetchStrategies } =
    useUserStrategies();

  logger.log('NewStrategy:Init', {
    strategy: newStrategy?.strategyName,
    flow: newStrategy?.flow,
  });

  const isLastStep = currentStep === flowSteps.length - 1;

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const saveButtonName: FooterNavigatorProps['saveButtonName'] = isLastStep
    ? 'Confirm'
    : 'Continue';

  const handleBackButton = () => {
    dispatch({
      currentStep: currentStep - 1,
    });
  };

  const goToNextStep = () => {
    dispatch({
      currentStep: currentStep + 1,
    });
  };

  const handleContinueClick = () => {
    logger.log('NewStrategy:handleContinueClick', {
      flow,
      currentStep,
      key: flowSteps[currentStep].key,
    });

    setIsLoading(true);

    if (flowSteps[currentStep].key === 'CREATE_STRATEGY') {
      const input = {
        name: newStrategy.strategyName,
        currency: newStrategy.reportingCurrency?.code,
        color: newStrategy.strategyColor,
        tags: newStrategy.tagList,
        startDate: newStrategy.strategyTimeframe[0]
          ? formatAWSDate(newStrategy.strategyTimeframe[0])
          : null,
        endDate: newStrategy.strategyTimeframe[1]
          ? formatAWSDate(newStrategy.strategyTimeframe[1])
          : null,
        notifyEnd: newStrategy.timeframeAlert,
      };

      // if strategy was already created update it else create new one
      if (!!newStrategy.strategyId) {
        input['id'] = newStrategy.strategyId;

        updateStrategy!({
          variables: {
            input,
          },
        })
          .then(() => refetchStrategies())
          .then(() => goToNextStep())
          .catch((err) => logger.error('NewStrategy:updateStrategy', err))
          .finally(() => setIsLoading(false));
      } else {
        input['profileId'] = user?.profile?.id;
        input['active'] = user?.profile?.creationStatus !== 'COMPLETED';
        input['defaultWorkspaceId'] = user?.profile?.defaultWorkspaceId;
        input['flow'] = flow;

        createStrategy!({ variables: { input } })
          .then(() => refetchStrategies())
          .then(() => goToNextStep())
          .catch((err) => logger.error('NewStrategy:createStrategy', err))
          .finally(() => setIsLoading(false));
      }
    } else if (flowSteps[currentStep].key === 'IMPORT_MODE') {
      setStrategyStatus!({
        variables: {
          strategyId: newStrategy.strategyId,
          status: `ADD_ASSETS_${mode}`,
        },
      })
        .then(() => refetchStrategies())
        .then(() => goToNextStep())
        .finally(() => setIsLoading(false));
    } else if (flowSteps[currentStep].key === 'ADD_FROM_STRATEGIES') {
      const newStrategyAssetsIds = newStrategy.assets.map(({ id }) => id);

      const strategyAssetsToCreate = newStrategy.assets.filter(
        (strategyAsset) => {
          return strategyAsset.strategyId !== newStrategy.strategyId;
        }
      );

      const strategyAssetsToDelete =
        draftCloneStrategy?.strategyAssets.items.filter((strategyAsset) => {
          return !newStrategyAssetsIds.includes(strategyAsset.id);
        }) || [];

      logger.log('NewStrategy:UpdateStrategyAfterImport', {
        newStrategyAssets: newStrategy.assets.length,
        draftCloneStrategyAssets:
          draftCloneStrategy?.strategyAssets.items.length,
        create: strategyAssetsToCreate.length,
        delete: strategyAssetsToDelete?.length,
      });

      const input = strategyAssetsToCreate.map((strategyAsset) => {
        return {
          alert: strategyAsset?.alert,
          assetId: strategyAsset?.assetId,
          bank: strategyAsset?.bank,
          connectionType: strategyAsset?.connectionType,
          currency: newStrategy?.reportingCurrency.code,
          entryPrice: strategyAsset?.entryPrice,
          inspirationSource: strategyAsset?.inspirationSource,
          name: strategyAsset?.name,
          profileId: user?.profile?.id,
          purchasedAt: strategyAsset?.purchasedAt,
          strategyAssetCategoryId: strategyAsset?.strategyAssetCategoryId,
          strategyAssetTargets: strategyAsset.strategyAssetTargets.items.map(
            (strategyAssetTarget) => {
              return {
                alert: false,
                endDate: strategyAssetTarget.endDate,
                stopLoss: strategyAssetTarget.stopLoss,
                stopProfit: strategyAssetTarget.stopProfit,
              };
            }
          ),
          strategyId: newStrategy.strategyId,
          unit: strategyAsset?.unit,
          wallet: strategyAsset?.wallet,
        };
      });

      Promise.resolve()
        .then(() => {
          return (
            strategyAssetsToDelete.length &&
            deleteStrategyAssets!({
              variables: {
                strategyAssetIds: strategyAssetsToDelete.map(({ id }) => id),
                strategyId: newStrategy.strategyId,
              },
            })
          );
        })
        .then(() => {
          return (
            strategyAssetsToCreate.length &&
            createStrategyAssets!({
              variables: {
                input,
              },
            })
          );
        })
        .then(() => refetchStrategies())
        .then(() => goToNextStep())
        .finally(() => setIsLoading(false));
    } else if (flowSteps[currentStep].key === 'ADD_ASSETS') {
      updateWorkspace!({
        variables: {
          targetWorkspaceId: workspacesList?.[0]?.id,
          currentWorkspaceId: newStrategy.currentWorkspaceId,
          strategyId: newStrategy.strategyId,
          profileId: user?.profile?.id,
        },
      })
        .then(() => refetchStrategies())
        .then(() => {
          if (user?.profile?.creationStatus !== 'COMPLETED') {
            navigate(`/registration-completed`);
          } else {
            navigate(`/strategies/${newStrategy.strategyId}`);
          }

          closeModal!();
        })
        .catch((err) => logger.error('NewStrategy:updateWorkspace', err))
        .finally(() => setIsLoading(false));
    } else {
      goToNextStep();
      setIsLoading(false);
    }
  };

  const BetweenButtonsElement = () =>
    currentStep && newStrategy.strategyId ? (
      <span className="mx-auto">
        <Button
          buttonClass="font-bold text-grey-50"
          type="button"
          onClick={() => dispatch({ confirmElement: <Skip /> })}
        >
          {t('strategies.newStrategy.skipAndDoItLater')}
        </Button>
      </span>
    ) : (
      <></>
    );

  const renderStep = () => {
    const step = flowSteps[currentStep];
    logger.log('NewStrategy:RenderStep', { flow, step: step.key });
    switch (step.key) {
      case 'START':
        return <Start />;
      case 'CREATE_STRATEGY':
        return <Customize />;
      case 'IMPORT_MODE':
        return <ChooseImportMode />;
      case 'ADD_FROM_STRATEGIES':
        return <DuplicateOrImport />;
      case 'ADD_ASSETS':
        return <Summary />;
      default:
        return <></>;
    }
  };

  return (
    <div className="flex flex-col h-full w-full">
      <Modal
        element={confirmElement || <></>}
        isOpen={typeof confirmElement !== 'undefined'}
        onCloseClick={closeConfirmModal}
      />

      <Nav />

      <div className="flex h-full w-full overflow-auto bg-white dark:bg-black-real">
        {renderStep()}
      </div>

      <FooterNavigator
        betweenButtons={<BetweenButtonsElement />}
        buttonsPosition={currentStep ? 'between' : 'end'}
        cancelButtonClassName="border-none text-center"
        cancelButtonName="Back"
        cancelButtonSVG={
          <Icon svgName="arrowBack" className="mr-2" canUseDarkMode={false} />
        }
        className="h-24 border-t-1 border-grey-10 dark:border-grey-80"
        isDisabledSaveButton={isDisabledNext}
        onCancel={handleBackButton}
        oneButton={!currentStep}
        saveButtonName={saveButtonName}
        onSave={handleContinueClick}
        loading={isLoading}
      />
    </div>
  );
};
